/**
 * useGridLayoutPersistence Hook
 * Manages localStorage persistence of grid layout configurations
 * Includes validation, version control, and debounced saves
 */

import { useState, useCallback, useEffect } from 'react';
import { STORAGE_KEY, STORAGE_VERSION } from '../utils/layoutConstants';

/**
 * useGridLayoutPersistence - Custom hook for managing layout persistence
 * 
 * @param {Object} defaultLayouts - Default layout configuration to use as fallback
 * @returns {Object} Object containing:
 *   - layouts: Current layout state
 *   - setLayouts: Function to update layouts
 *   - saveLayout: Function to save layout changes
 *   - resetLayout: Function to reset to defaults
 */
function useGridLayoutPersistence(defaultLayouts) {
    const [layouts, setLayouts] = useState(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (!stored) {
                return defaultLayouts;
            }

            const parsed = JSON.parse(stored);

            // Version check
            if (parsed.version !== STORAGE_VERSION) {
                console.warn('Layout version mismatch, using defaults');
                return defaultLayouts;
            }

            // Validate that all required chart IDs are present
            const isValid = validateLayouts(parsed.layouts, defaultLayouts);
            if (!isValid) {
                console.warn('Layout validation failed, using defaults');
                return defaultLayouts;
            }

            return parsed.layouts;
        } catch (error) {
            console.error('Failed to load layouts from localStorage:', error);
            return defaultLayouts;
        }
    });

    // Debounced save to localStorage
    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                const toStore = {
                    version: STORAGE_VERSION,
                    timestamp: Date.now(),
                    layouts: layouts
                };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
            } catch (error) {
                console.error('Failed to save layouts to localStorage:', error);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timer);
    }, [layouts]);

    const saveLayout = useCallback((newLayouts) => {
        setLayouts(newLayouts);
    }, []);

    const resetLayout = useCallback(() => {
        setLayouts(defaultLayouts);
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            console.error('Failed to clear localStorage:', error);
        }
    }, [defaultLayouts]);

    return {
        layouts,
        setLayouts,
        saveLayout,
        resetLayout
    };
}

/**
 * Validate that layouts contain all required chart IDs
 * 
 * @param {Object} layouts - Layouts to validate
 * @param {Object} defaultLayouts - Default layouts to compare against
 * @returns {boolean} True if layouts are valid
 */
function validateLayouts(layouts, defaultLayouts) {
    try {
        // Check that all breakpoints exist
        const defaultBreakpoints = Object.keys(defaultLayouts);

        for (const bp of defaultBreakpoints) {
            if (!layouts[bp]) {
                return false;
            }

            // Check that all chart IDs are present
            const defaultIds = new Set(defaultLayouts[bp].map(item => item.i));
            const layoutIds = new Set(layouts[bp].map(item => item.i));

            if (defaultIds.size !== layoutIds.size) {
                return false;
            }

            for (const id of defaultIds) {
                if (!layoutIds.has(id)) {
                    return false;
                }
            }
        }

        return true;
    } catch (error) {
        console.error('Layout validation error:', error);
        return false;
    }
}

export default useGridLayoutPersistence;
